import React, { useState, useEffect, useCallback } from "react";
import styles from "../../../../styles/LmsForm.module.css";
import closeIcon from "../../../../assets/close.png";
import { notification, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { db, storage } from "../../../../firebase-config";
import { collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import ButtonComponent from "../../../CssComponents/ButtonComponent/ButtonComponent";
import MediaPreviewModal from "../../../CssComponents/MediaPreviewModal/MediaPreviewModal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

function FormModule({ initialValues, setInitialValues, showForm, onClose }) {
  const [bannerFile, setBannerFile] = useState(null);
  const [contentFile, setContentFile] = useState(null);
  const [digitalVideoFile, setDigitalVideoFile] = useState(null);

  const [bannerFileList, setBannerFileList] = useState([]);
  const [contentFileList, setContentFileList] = useState([]);
  const [digitalVideoFileList, setDigitalVideoFileList] = useState([]);

  const [previewFile, setPreviewFile] = useState({ url: "", type: "" });
  const [previewVisible, setPreviewVisible] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Initialize file lists for existing module
    if (initialValues.moduleBanner) {
      setBannerFileList([
        {
          uid: initialValues.moduleId || "-1",
          name: "existing_banner",
          status: "done",
          url: initialValues.moduleBanner,
        },
      ]);
    }
    if (initialValues.moduleContent) {
      setContentFileList([
        {
          uid: initialValues.moduleId || "-2",
          name: "existing_content",
          status: "done",
          url: initialValues.moduleContent,
        },
      ]);
    }
    if (initialValues.moduleVideo) {
      setDigitalVideoFileList([
        {
          uid: initialValues.moduleId || "-3",
          name: "existing_digital_video",
          status: "done",
          url: initialValues.moduleVideo,
        },
      ]);
    }

    // Cleanup
    return () => {
      setBannerFileList([]);
      setContentFileList([]);
      setDigitalVideoFileList([]);
    };
  }, [
    initialValues.moduleBanner,
    initialValues.moduleContent,
    initialValues.moduleVideo,
    initialValues.moduleId,
  ]);

  // Updated handlePreview function
  const handlePreview = useCallback(async (file) => {
    const fileUrl = file.url || file.thumbUrl;
    if (fileUrl) {
      const fileType =
        file.type ||
        (file.name ? file.name.split(".").pop().toLowerCase() : "");
      let previewType = "image";
      if (
        fileType.includes("video") ||
        fileUrl.includes(".mp4") ||
        fileUrl.includes(".webm")
      ) {
        previewType = "video";
      } else if (fileType.includes("pdf") || fileUrl.includes(".pdf")) {
        previewType = "pdf";
      }
      setPreviewFile({ url: fileUrl, type: previewType });
      setPreviewVisible(true);
    }
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setInitialValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [setInitialValues]
  );

  const validateFile = (file, type) => {
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (!file) return false;
    if (file.size > maxSize) {
      notification.error({
        message: "File too large",
        description: "File must be smaller than 100MB",
      });
      return false;
    }
    return true;
  };

  const uploadToFirebaseStorage = async (file, path) => {
    try {
      if (!validateFile(file, path)) {
        throw new Error("Invalid file");
      }

      const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error("Upload error:", error);
      throw new Error("Failed to upload file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let bannerUrl = initialValues.moduleBanner || "";
      let contentUrl = initialValues.moduleContent || "";
      let digitalVideoUrl = initialValues.moduleVideo || "";
      const isUpdate = !!initialValues.moduleId;

      console.log("Banner file state:", bannerFile);
      console.log("Content file state:", contentFile);
      console.log("Digital video file state:", digitalVideoFile);

      if (bannerFile && bannerFile instanceof File) {
        bannerUrl = await uploadToFirebaseStorage(
          bannerFile,
          "lms_module_thumbnail"
        );
      }
      if (contentFile && contentFile instanceof File) {
        contentUrl = await uploadToFirebaseStorage(
          contentFile,
          "lms_module_content"
        );
      }
      if (digitalVideoFile && digitalVideoFile instanceof File) {
        digitalVideoUrl = await uploadToFirebaseStorage(
          digitalVideoFile,
          "lms_module_digital_video"
        );
      }

      const moduleData = {
        ...initialValues,
        moduleBanner: bannerUrl,
        moduleContent: contentUrl,
        moduleVideo: digitalVideoUrl,
        updatedAt: new Date().toISOString(),
      };

      console.log("Saving module data:", moduleData);
      console.log("Module type being saved:", moduleData.moduleType);

      if (isUpdate) {
        await updateDoc(
          doc(db, "lms_module", initialValues.moduleId),
          moduleData
        );
        notification.success({
          message: "Module Updated",
          description: "Module updated successfully.",
          placement: "topRight",
          duration: 3,
        });
      } else {
        const docRef = await addDoc(collection(db, "lms_module"), moduleData);
        await updateDoc(doc(db, "lms_module", docRef.id), {
          moduleId: docRef.id,
          createdAt: new Date().toISOString(),
        });
        notification.success({
          message: "Module Created",
          description: "New module created successfully.",
          placement: "topRight",
          duration: 3,
        });
      }

      onClose();
      setInitialValues({});
      setBannerFile(null);
      setContentFile(null);
      setDigitalVideoFile(null);
      setBannerFileList([]);
      setContentFileList([]);
      setDigitalVideoFileList([]);
    } catch (error) {
      console.error("Error saving module:", error);
      notification.error({
        message: "Error",
        description:
          error.message || "Failed to save module. Please try again.",
        placement: "topRight",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBannerChange = ({ fileList }) => {
    setBannerFileList(fileList);
    const file = fileList.length > 0 ? fileList[0].originFileObj : null;
    console.log("Banner file selected:", file);
    setBannerFile(file);
  };

  const handleContentChange = ({ fileList }) => {
    setContentFileList(fileList);
    const file = fileList.length > 0 ? fileList[0].originFileObj : null;
    console.log("Content file selected:", file);
    setContentFile(file);
  };

  const handleDigitalVideoChange = ({ fileList }) => {
    setDigitalVideoFileList(fileList);
    const file = fileList.length > 0 ? fileList[0].originFileObj : null;
    setDigitalVideoFile(file);
  };

  return (
    <div
      className={
        showForm
          ? `${styles.modal} ${styles.displayBlock}`
          : `${styles.modal} ${styles.displayNone}`
      }
    >
      <div className={styles.modalMain}>
        <button
          className={styles.closeBtn}
          onClick={onClose}
          disabled={isLoading}
        >
          <img src={closeIcon} alt="Close" />
        </button>
        <div className={styles.mainContent}>
          <div className={styles.title}>Module</div>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Module Name</div>
              <input
                name="moduleName"
                className={styles.input}
                value={initialValues.moduleName || ""}
                onChange={handleChange}
                placeholder="Enter Module Name"
                required
                disabled={isLoading}
              />
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Module Description</div>
              <ReactQuill
                theme="snow"
                value={initialValues.moduleDescription || ""}
                onChange={(value) =>
                  handleChange({ target: { name: "moduleDescription", value } })
                }
                placeholder="Enter Module Description"
                readOnly={isLoading}
              />
            </div>

            {/* Conditional rendering for module type */}
            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Module Type</div>
              <select
                name="moduleType"
                className={styles.input}
                value={initialValues.moduleType || ""}
                onChange={handleChange}
                required
                disabled={isLoading}
              >
                <option value="">Select Module Type</option>
                <option value="live">Live</option>
                <option value="digital">Digital</option>
              </select>
            </div>

            {initialValues.moduleType === "live" && (
              <>
                <div className={styles.labelInputWrapper}>
                  <div className={styles.labelText}>Live Session Link</div>
                  <input
                    name="liveLink"
                    className={styles.input}
                    value={initialValues.liveLink || ""}
                    onChange={handleChange}
                    placeholder="Enter live session link (e.g. Zoom/Meet)"
                    required
                    disabled={isLoading}
                  />
                </div>
              </>
            )}

            {/* Module Content (Video or PDF) - always show */}
            {initialValues.moduleType === "live" && (
              <div className={styles.labelInputWrapper}>
                <div className={styles.labelText}>
                  Module Content (Video or PDF)
                </div>
                <Upload
                  accept="video/*,.pdf"
                  listType="picture-card"
                  beforeUpload={() => false}
                  fileList={contentFileList}
                  onChange={handleContentChange}
                  onPreview={handlePreview}
                  showUploadList={{ showRemoveIcon: true }}
                  maxCount={1}
                  disabled={isLoading}
                >
                  {contentFileList.length < 1 && (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>
                        Upload <br /> Content
                      </div>
                    </div>
                  )}
                </Upload>
              </div>
            )}
            {/* Module Video (Digital) - only for digital type */}
            {initialValues.moduleType === "digital" && (
              <div className={styles.labelInputWrapper}>
                <div className={styles.labelText}>Module Video (Digital)</div>
                <Upload
                  accept="video/*"
                  listType="picture-card"
                  beforeUpload={() => false}
                  fileList={digitalVideoFileList}
                  onChange={handleDigitalVideoChange}
                  onPreview={handlePreview}
                  showUploadList={{ showRemoveIcon: true }}
                  maxCount={1}
                  disabled={isLoading}
                >
                  {digitalVideoFileList.length < 1 && (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Upload Video</div>
                    </div>
                  )}
                </Upload>
              </div>
            )}

            {/* Thumbnail upload below Module Content */}
            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Module Thumbnail</div>
              <Upload
                accept="image/*"
                listType="picture-card"
                beforeUpload={() => false}
                fileList={bannerFileList}
                onChange={handleBannerChange}
                onPreview={handlePreview}
                showUploadList={{ showRemoveIcon: true }}
                maxCount={1}
                disabled={isLoading}
              >
                {bannerFileList.length < 1 && (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Upload Thumbnail</div>
                  </div>
                )}
              </Upload>
            </div>

            <ButtonComponent
              buttonText={
                initialValues.moduleId ? "Update Module" : "Save Module"
              }
              buttonType="submit"
              disabled={isLoading}
              loading={isLoading}
            />
          </form>
        </div>
      </div>

      <MediaPreviewModal
        previewVisible={previewVisible}
        setPreviewVisible={setPreviewVisible}
        previewFile={previewFile}
      />
    </div>
  );
}

export default FormModule;
